export const setupSignatureProtection = (editorInstance: any) => {
  try {
    if (!editorInstance || !editorInstance.events) return;

    // Make all signatures completely non-editable
    const makeSignaturesNonEditable = () => {
      const signatures = editorInstance.el.querySelectorAll('.signature');
      if (!signatures.length) return;

      signatures.forEach((signature: HTMLElement) => {
        // Set contentEditable to false to prevent cursor placement
        signature.contentEditable = 'false';
        signature.setAttribute('data-non-editable', 'true');

        const allChildren = signature.querySelectorAll('*');
        allChildren.forEach((child: HTMLElement) => {
          child.contentEditable = 'false';

          // Disable specific interactive elements
          if (child.tagName === 'IMG') {
            child.style.pointerEvents = 'none';
            child.setAttribute('data-fr-image-editable', 'false');
          }
        });

        const computedStyle = window.getComputedStyle(signature);
        if (computedStyle.position === 'static') {
          signature.style.position = 'relative';
        }

        // Add a transparent overlay to handle selection without editing
        if (!signature.querySelector('.signature-overlay')) {
          const overlay = document.createElement('div');
          overlay.className = 'signature-overlay';
          Object.assign(overlay.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            zIndex: '1',
            pointerEvents: 'none',
          });
          signature.appendChild(overlay);
        }

        // Set a custom attribute for our event handlers
        signature.setAttribute('data-signature-protected', 'true');
      });
    };

    // Apply non-editable to signatures on initialization
    makeSignaturesNonEditable();

    editorInstance.events.on('contentChanged', makeSignaturesNonEditable);
    editorInstance.events.on('html.set', makeSignaturesNonEditable);

    editorInstance.events.on('image.beforeRemove', function (img: HTMLElement) {
      return !isSignatureImage(img);
    });

    editorInstance.events.on('image.clicked', function (img: HTMLElement) {
      if (isSignatureImage(img)) {
        // Prevent image popup from appearing
        this.popups?.hideAll?.();
        // Remove image selection
        this.selection?.clear?.();
        return false;
      }
    });

    editorInstance.events.on('link.clicked', function (linkEl: HTMLElement) {
      if (linkEl.closest('.signature')) {
        // Prevent link popup from appearing
        this.popups?.hideAll?.();
        // Remove link selection but preserve the href behavior for opening links
        this.selection?.clear?.();
        return true; // Allow native behavior (e.g. opening link)
      }
    });

    // Simple handler to prevent any commands that could modify signatures
    editorInstance.events.on('commands.before', function (cmd: string) {
      try {
        const allowed = ['selectAll', 'copy'];
        if (allowed.includes(cmd)) return true;

        const el = this.selection?.element?.();
        return !el?.closest?.('.signature');
      } catch (err) {
        console.warn('Error in commands.before handler', err);
        return true;
      }
    });

    // Hide all popups when interacting with signatures
    editorInstance.events.on('popups.show', function ($popup: any) {
      try {
        const el = editorInstance.selection?.element?.();
        if (el?.closest?.('.signature')) {
          $popup.hide();
          return false;
        }
      } catch (err) {
        console.warn('Error handling popup in signature', err);
      }
    });

    // Prevent cut/paste operations involving signatures
    const preventEditOperation = () => {
      try {
        const el = editorInstance.selection?.element?.();
        return !el?.closest?.('.signature');
      } catch (err) {
        console.warn('Error preventing edit operation in signature', err);
        return true;
      }
    };

    editorInstance.events.on('cut.before', preventEditOperation);
    editorInstance.events.on('paste.before', preventEditOperation);
  } catch (err) {
    console.warn('Failed to register signature protection handlers', err);
  }
};

function isSignatureImage(img: HTMLElement) {
  if (!img || !img[0]) return false;

  return img[0].getAttribute('data-signature-image') === 'true';
}
